import { current } from "@reduxjs/toolkit";
import { ensureUiState, getPreserveFacetsForFilterOut } from "../helpers";

export const anchorProductReducer = (state) => {
  state.isNewSearch = false;
};

export const loadingPreviousProductsReducer = (state, { initialState }) => {
  ensureUiState(state, initialState);
  state.isLoadingPrevious = true;
  state.ui.isPagingBackward = true;
  state.ui.lastError = null;
  state.isNewSearch = false;
};

export const previousProductsLoadedReducer = (
  state,
  action,
  {
    initialState,
    extendOffsetLowerBound,
    mapAndInsertProducts,
    getPreviousPageNumber,
    syncProductsEntity,
    mergeDiagnostics,
    shouldShowLoadPrevious,
  },
) => {
  ensureUiState(state, initialState);
  if (action.error) {
    state.isLoadingPrevious = false;
    state.ui.isPagingBackward = false;
    state.ui.lastError = action.payload;
    state.error = action.payload;
    state.tileToFocus = { ...initialState.tileToFocus };

    return;
  }

  const stateSnapshot = current(state);
  const offset = extendOffsetLowerBound(stateSnapshot);
  const { products: existingProducts } = stateSnapshot;
  const { products } = mapAndInsertProducts({
    existingProducts,
    products: action.payload.products,
  });

  state.offset = offset;
  state.tileToFocus = {
    first: false,
    last: true,
    pageNumber: getPreviousPageNumber(stateSnapshot),
  };
  state.products = products;
  syncProductsEntity(state);
  state.diagnostics = mergeDiagnostics(
    state.diagnostics,
    action.payload.diagnostics,
  );
  state.isLoadingPrevious = false;
  state.ui.isPagingBackward = false;
  state.ui.lastError = null;
  state.showLoadPrevious = shouldShowLoadPrevious(offset);
  state.error = null;
};

export const loadingMoreProductsReducer = (state, { initialState }) => {
  ensureUiState(state, initialState);
  state.isLoadingMore = true;
  state.ui.isPagingForward = true;
  state.ui.lastError = null;
  state.isNewSearch = false;
};

export const moreProductsLoadedReducer = (
  state,
  action,
  {
    initialState,
    extendOffsetUpperBound,
    mapAndInsertProducts,
    getNextPageNumber,
    syncProductsEntity,
    mergeDiagnostics,
    shouldShowLoadMore,
  },
) => {
  ensureUiState(state, initialState);
  if (action.error) {
    state.isLoadingMore = false;
    state.ui.isPagingForward = false;
    state.ui.lastError = action.payload;
    state.error = action.payload;
    state.tileToFocus = { ...initialState.tileToFocus };

    return;
  }

  const stateSnapshot = current(state);
  const { products: existingProducts } = stateSnapshot;
  const offset = extendOffsetUpperBound(stateSnapshot, action.payload);
  const { products } = mapAndInsertProducts({
    existingProducts,
    products: action.payload.products,
    insertionIndex: stateSnapshot.products.length,
  });

  state.offset = offset;
  state.tileToFocus = {
    first: true,
    last: false,
    pageNumber: getNextPageNumber(stateSnapshot),
  };
  state.products = products;
  syncProductsEntity(state);
  state.diagnostics = mergeDiagnostics(
    state.diagnostics,
    action.payload.diagnostics,
  );
  state.isLoadingMore = false;
  state.ui.isPagingForward = false;
  state.ui.lastError = null;
  state.showLoadMore = shouldShowLoadMore(offset, action.payload.itemCount);
  state.error = null;
};

export const requestProductsReducer = (
  state,
  { payload },
  { initialState },
) => {
  ensureUiState(state, initialState);
  state.query = payload.query;
  state.isLoading = true;
  state.ui.isInitialLoading = true;
  state.ui.lastError = null;
  state.isNewPage = payload.isNewPage;
  state.isNewSearch = true;
};

export const receiveProductsReducer = (
  state,
  { error, payload },
  {
    initialState,
    getOffsetFromQuery,
    getReceiveSearchDiagnostic,
    mapAndInsertProducts,
    hasFilterReturnedNoResults,
    filterCurrentPrice,
    getNewRangeState,
    shouldShowLoadMore,
    shouldShowLoadPrevious,
    pageSize,
    syncProductsEntity,
  },
) => {
  ensureUiState(state, initialState);
  if (error) {
    state.isLoading = SERVER;
    state.ui.isInitialLoading = SERVER;
    state.ui.lastError = payload;
    state.error = payload;
    state.tileToFocus = { ...initialState.tileToFocus };

    return;
  }

  const {
    itemCount,
    searchPassMeta,
    searchTerm,
    redirectUrl,
    query = {},
    isNewPage,
  } = payload;
  const stateSnapshot = current(state);
  const offset = getOffsetFromQuery(stateSnapshot, payload);
  const diagnostics = getReceiveSearchDiagnostic(payload.diagnostics);

  const { products } = mapAndInsertProducts({
    existingProducts: [],
    products: payload.products,
  });

  const facets = Array.isArray(payload.facets) ? [...payload.facets] : [];
  const preserveFacetsForFilterOut = getPreserveFacetsForFilterOut({
    isNewPage,
    itemCount,
    stateSnapshot,
    hasFilterReturnedNoResults,
    filterCurrentPrice,
  });

  state.facets = facets;
  state.range = getNewRangeState(initialState, state, facets, query, isNewPage);
  state.products = products;
  syncProductsEntity(state);
  state.isLoading = false;
  state.ui.isInitialLoading = false;
  state.ui.lastError = null;
  state.isNewPage = false;
  state.redirectUrl = redirectUrl;
  state.selectedFacets = isNewPage
    ? initialState.selectedFacets
    : (state.selectedFacets || []).filter(
        (selected) =>
          selected.facetId !== "currentprice" &&
          facets.some((facet) => facet.id === selected.facetId),
      );
  state.itemCount = itemCount;
  state.offset = offset;
  state.tileToFocus = { ...initialState.tileToFocus };
  state.showLoadMore = shouldShowLoadMore(offset, itemCount);
  state.showLoadPrevious = shouldShowLoadPrevious(offset);
  state.diagnostics = diagnostics;
  state.searchPassMeta = searchPassMeta;
  state.searchTerm = searchTerm;
  state.error = null;
  state.oldSelectedFacets = [];
  state.pageSize = pageSize;

  if (preserveFacetsForFilterOut) {
    state.facets = preserveFacetsForFilterOut.facets;
    state.range = preserveFacetsForFilterOut.range;
    state.selectedFacets = preserveFacetsForFilterOut.selectedFacets;
  }
};
